import { arcToCubics } from './arc';
import type { Segment } from './segments';

/**
 * Strict SVG path-data parser (SVG 2 path grammar) that normalizes every command to
 * absolute M/L/C/Z segments. It is a character scanner, not a regular expression, and
 * rejects malformed input instead of rendering up to the first error.
 */
export interface PathParseOptions {
  /** Maximum number of commands (including implicit repeats). docs/10 proposes 20,000 per asset. */
  readonly maxCommands?: number;
  /** Maximum absolute coordinate or radius value accepted. */
  readonly maxAbsValue?: number;
}

export const DEFAULT_MAX_PATH_COMMANDS = 20_000;
export const DEFAULT_MAX_ABS_PATH_VALUE = 1_000_000;

export class PathSyntaxError extends Error {
  readonly position: number;
  constructor(message: string, position: number) {
    super(`${message} (at character ${position})`);
    this.name = 'PathSyntaxError';
    this.position = position;
  }
}

export type PathParseResult =
  | { readonly ok: true; readonly segments: Segment[] }
  | { readonly ok: false; readonly error: PathSyntaxError };

export function tryParsePath(d: string, options: PathParseOptions = {}): PathParseResult {
  try {
    return { ok: true, segments: parsePath(d, options) };
  } catch (error) {
    if (error instanceof PathSyntaxError) return { ok: false, error };
    throw error;
  }
}

export function parsePath(d: string, options: PathParseOptions = {}): Segment[] {
  const maxCommands = options.maxCommands ?? DEFAULT_MAX_PATH_COMMANDS;
  const maxAbs = options.maxAbsValue ?? DEFAULT_MAX_ABS_PATH_VALUE;
  const s = new Scanner(d, maxAbs);
  const out: Segment[] = [];

  let curX = 0;
  let curY = 0;
  let startX = 0;
  let startY = 0;
  let lastCubic: { x: number; y: number } | null = null;
  let lastQuad: { x: number; y: number } | null = null;
  let closed = false;
  let commands = 0;

  const ensureMoveAfterClose = () => {
    if (closed) {
      out.push({ type: 'M', x: curX, y: curY });
      closed = false;
    }
  };

  s.skipWsp();
  if (s.atEnd()) throw new PathSyntaxError('Path data is empty', 0);

  let first = true;
  while (true) {
    s.skipWsp();
    if (s.atEnd()) break;
    const position = s.index;
    const letter = s.peek();
    if (!isCommandLetter(letter)) {
      throw new PathSyntaxError(`Expected a path command but found "${letter}"`, position);
    }
    s.advance();
    if (first && letter !== 'M' && letter !== 'm') {
      throw new PathSyntaxError('Path data must begin with a moveto command', position);
    }
    first = false;

    const upper = letter.toUpperCase();
    const relative = letter !== upper;

    if (upper === 'Z') {
      commands += 1;
      if (commands > maxCommands) throw tooComplex(position, maxCommands);
      out.push({ type: 'Z' });
      curX = startX;
      curY = startY;
      lastCubic = null;
      lastQuad = null;
      closed = true;
      s.skipWsp();
      if (!s.atEnd() && s.peekIsNumberStart()) {
        throw new PathSyntaxError('A closepath command takes no parameters', s.index);
      }
      continue;
    }

    let firstSet = true;
    do {
      commands += 1;
      if (commands > maxCommands) throw tooComplex(s.index, maxCommands);
      const ox = relative ? curX : 0;
      const oy = relative ? curY : 0;
      switch (upper) {
        case 'M': {
          const x = s.readCoordinate() + ox;
          const y = s.readCoordinate() + oy;
          if (firstSet) {
            out.push({ type: 'M', x, y });
            startX = x;
            startY = y;
            closed = false;
          } else {
            ensureMoveAfterClose();
            out.push({ type: 'L', x, y }); // implicit lineto after moveto
          }
          curX = x;
          curY = y;
          lastCubic = null;
          lastQuad = null;
          break;
        }
        case 'L': {
          const x = s.readCoordinate() + ox;
          const y = s.readCoordinate() + oy;
          ensureMoveAfterClose();
          out.push({ type: 'L', x, y });
          curX = x;
          curY = y;
          lastCubic = null;
          lastQuad = null;
          break;
        }
        case 'H': {
          const x = s.readCoordinate() + ox;
          ensureMoveAfterClose();
          out.push({ type: 'L', x, y: curY });
          curX = x;
          lastCubic = null;
          lastQuad = null;
          break;
        }
        case 'V': {
          const y = s.readCoordinate() + oy;
          ensureMoveAfterClose();
          out.push({ type: 'L', x: curX, y });
          curY = y;
          lastCubic = null;
          lastQuad = null;
          break;
        }
        case 'C': {
          const x1 = s.readCoordinate() + ox;
          const y1 = s.readCoordinate() + oy;
          const x2 = s.readCoordinate() + ox;
          const y2 = s.readCoordinate() + oy;
          const x = s.readCoordinate() + ox;
          const y = s.readCoordinate() + oy;
          ensureMoveAfterClose();
          out.push({ type: 'C', x1, y1, x2, y2, x, y });
          lastCubic = { x: x2, y: y2 };
          lastQuad = null;
          curX = x;
          curY = y;
          break;
        }
        case 'S': {
          const x2 = s.readCoordinate() + ox;
          const y2 = s.readCoordinate() + oy;
          const x = s.readCoordinate() + ox;
          const y = s.readCoordinate() + oy;
          const x1 = lastCubic ? 2 * curX - lastCubic.x : curX;
          const y1 = lastCubic ? 2 * curY - lastCubic.y : curY;
          ensureMoveAfterClose();
          out.push({ type: 'C', x1, y1, x2, y2, x, y });
          lastCubic = { x: x2, y: y2 };
          lastQuad = null;
          curX = x;
          curY = y;
          break;
        }
        case 'Q': {
          const qx = s.readCoordinate() + ox;
          const qy = s.readCoordinate() + oy;
          const x = s.readCoordinate() + ox;
          const y = s.readCoordinate() + oy;
          ensureMoveAfterClose();
          out.push(quadToCubic(curX, curY, qx, qy, x, y));
          lastQuad = { x: qx, y: qy };
          lastCubic = null;
          curX = x;
          curY = y;
          break;
        }
        case 'T': {
          const x = s.readCoordinate() + ox;
          const y = s.readCoordinate() + oy;
          const qx: number = lastQuad ? 2 * curX - lastQuad.x : curX;
          const qy: number = lastQuad ? 2 * curY - lastQuad.y : curY;
          ensureMoveAfterClose();
          out.push(quadToCubic(curX, curY, qx, qy, x, y));
          lastQuad = { x: qx, y: qy };
          lastCubic = null;
          curX = x;
          curY = y;
          break;
        }
        case 'A': {
          const rx = s.readCoordinate();
          const ry = s.readCoordinate();
          const rotationDeg = s.readCoordinate();
          const largeArc = s.readFlag();
          const sweep = s.readFlag();
          const x = s.readCoordinate() + ox;
          const y = s.readCoordinate() + oy;
          ensureMoveAfterClose();
          out.push(...arcToCubics(curX, curY, rx, ry, rotationDeg, largeArc, sweep, x, y));
          lastCubic = null;
          lastQuad = null;
          curX = x;
          curY = y;
          break;
        }
        default:
          throw new PathSyntaxError(`Unsupported path command "${letter}"`, position);
      }
      firstSet = false;
      s.skipCommaWsp();
    } while (!s.atEnd() && s.peekIsNumberStart());
  }
  return out;
}

function quadToCubic(
  x0: number,
  y0: number,
  qx: number,
  qy: number,
  x: number,
  y: number,
): Segment {
  return {
    type: 'C',
    x1: x0 + (2 / 3) * (qx - x0),
    y1: y0 + (2 / 3) * (qy - y0),
    x2: x + (2 / 3) * (qx - x),
    y2: y + (2 / 3) * (qy - y),
    x,
    y,
  };
}

function tooComplex(position: number, max: number): PathSyntaxError {
  return new PathSyntaxError(`Path has more than ${max} commands`, position);
}

const COMMAND_LETTERS = new Set('MmZzLlHhVvCcSsQqTtAa');

function isCommandLetter(ch: string): boolean {
  return COMMAND_LETTERS.has(ch);
}

function isWsp(code: number): boolean {
  return code === 0x20 || code === 0x09 || code === 0x0a || code === 0x0c || code === 0x0d;
}

function isDigit(code: number): boolean {
  return code >= 0x30 && code <= 0x39;
}

class Scanner {
  index = 0;
  private readonly text: string;
  private readonly maxAbs: number;

  constructor(text: string, maxAbs: number) {
    this.text = text;
    this.maxAbs = maxAbs;
  }

  atEnd(): boolean {
    return this.index >= this.text.length;
  }

  peek(): string {
    return this.text.charAt(this.index);
  }

  advance(): void {
    this.index += 1;
  }

  skipWsp(): void {
    while (this.index < this.text.length && isWsp(this.text.charCodeAt(this.index))) {
      this.index += 1;
    }
  }

  skipCommaWsp(): void {
    this.skipWsp();
    if (this.text.charAt(this.index) === ',') {
      this.index += 1;
      this.skipWsp();
    }
  }

  peekIsNumberStart(): boolean {
    const code = this.text.charCodeAt(this.index);
    return isDigit(code) || code === 0x2e || code === 0x2b || code === 0x2d; // digit . + -
  }

  readCoordinate(): number {
    this.skipCommaWsp();
    const value = this.readNumber();
    if (Math.abs(value) > this.maxAbs) {
      throw new PathSyntaxError(`Path value ${value} exceeds the supported range`, this.index);
    }
    return value;
  }

  readFlag(): boolean {
    this.skipCommaWsp();
    const ch = this.text.charAt(this.index);
    if (ch !== '0' && ch !== '1') {
      throw new PathSyntaxError('Expected an arc flag (0 or 1)', this.index);
    }
    this.index += 1;
    return ch === '1';
  }

  private readNumber(): number {
    const start = this.index;
    const t = this.text;
    let i = this.index;
    if (t.charAt(i) === '+' || t.charAt(i) === '-') i += 1;
    let digits = 0;
    while (i < t.length && isDigit(t.charCodeAt(i))) {
      i += 1;
      digits += 1;
    }
    if (t.charAt(i) === '.') {
      i += 1;
      while (i < t.length && isDigit(t.charCodeAt(i))) {
        i += 1;
        digits += 1;
      }
    }
    if (digits === 0) throw new PathSyntaxError('Expected a number', start);
    const e = t.charAt(i);
    if (e === 'e' || e === 'E') {
      let j = i + 1;
      if (t.charAt(j) === '+' || t.charAt(j) === '-') j += 1;
      let expDigits = 0;
      while (j < t.length && isDigit(t.charCodeAt(j))) {
        j += 1;
        expDigits += 1;
      }
      if (expDigits === 0) throw new PathSyntaxError('Malformed number exponent', i);
      i = j;
    }
    const value = Number(t.slice(start, i));
    if (!Number.isFinite(value)) throw new PathSyntaxError('Number is not finite', start);
    this.index = i;
    return value;
  }
}
